import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Moves the store-wide offer track as the shopper changes the quantity or the cart. Tiers count
 * every item in the cart, store-wide. What the cart has already unlocked is kept apart from what
 * adding this section's product (at its selected quantity) would unlock, so the status never
 * claims an offer the cart does not have yet. Tier thresholds and status templates come from the
 * block's markup; the cart is only read (from cart update events, or `/cart.js` when an event
 * carries no cart), never written.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tiers - Tier steps carrying `data-quantity` and `data-title`, in order.
 * @property {HTMLElement} track - The progressbar element.
 * @property {HTMLElement} status - Live region holding the status sentence.
 *
 * @typedef {object} TierProgress
 * @property {HTMLElement | undefined} reached - Highest tier at or below the count.
 * @property {HTMLElement | undefined} next - Lowest tier above the count.
 * @property {number} fill - Track position for the count, from 0 to 1.
 *
 * @extends Component<Refs>
 */
class CpPromoComponent extends Component {
  requiredRefs = ['track', 'status'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number} */
  #cartCount = 0;

  /** @type {string | undefined} */
  #renderedKey;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });

    this.#cartCount = Number(this.dataset.cartCount) || 0;
    this.#renderedKey = undefined;
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    // A section re-render paints the server's state; reapply the live quantity and cart count.
    this.#renderedKey = undefined;
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Follows only the main product form in this block's section, not cart lines, Quick Add or
   * product cards.
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Element)) return;
    const detail = /** @type {{ quantity?: number, cartLine?: number } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    if (!detail || detail.cartLine) return;
    if (event.target.closest('product-card, dialog')) return;
    if (event.target.closest('.shopify-section') !== this.closest('.shopify-section')) return;

    this.#update(true);
  };

  /** @param {Event} event */
  #handleCartUpdate = async (event) => {
    const { promise } = /** @type {{ promise?: Promise<{ cart?: { totalQuantity?: number } | null }> }} */ (
      /** @type {unknown} */ (event)
    );

    try {
      const result = await promise;
      const total = result?.cart?.totalQuantity;
      if (typeof total === 'number') {
        this.#setCartCount(total);
      } else {
        await this.#refreshCartCount();
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      // A failed update may or may not have changed the cart; read it again.
      await this.#refreshCartCount().catch(() => {});
    }
  };

  /** Back/forward cache restores this page as it was; the cart may have changed elsewhere. */
  #handlePageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.#refreshCartCount().catch(() => {});
  };

  async #refreshCartCount() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      signal: this.#controller?.signal,
    });
    if (!response.ok) throw new Error(`Cart responded ${response.status}`);
    const cart = await response.json();
    this.#setCartCount(Number(cart.item_count) || 0);
  }

  /** @param {number} count */
  #setCartCount(count) {
    if (!this.isConnected) return;
    this.#cartCount = count;
    this.#update(true);
  }

  /** @returns {number} */
  #readSelectedQuantity() {
    const input = this.closest('.shopify-section')?.querySelector('product-form-component input[name="quantity"]');
    const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) : NaN;
    return value > 0 ? value : Number(this.dataset.selectedQuantity) || 1;
  }

  /**
   * @param {HTMLElement[]} tiers
   * @param {number} count
   * @returns {TierProgress}
   */
  #progressFor(tiers, count) {
    /** @type {TierProgress} */
    const progress = { reached: undefined, next: undefined, fill: 0 };
    let previousQuantity = 0;
    let previousPosition = 0;

    tiers.forEach((tier, index) => {
      const quantity = Number(tier.dataset.quantity);
      const position = (index + 1) / tiers.length;

      if (count >= quantity) {
        progress.reached = tier;
        progress.fill = position;
      } else if (!progress.next) {
        progress.next = tier;
        const span = quantity - previousQuantity;
        const step = span > 0 ? Math.max(count - previousQuantity, 0) / span : 0;
        progress.fill = previousPosition + (position - previousPosition) * step;
      }

      previousQuantity = quantity;
      previousPosition = position;
    });

    return progress;
  }

  /** @param {boolean} [animate] */
  #update(animate = false) {
    const selected = this.#readSelectedQuantity();
    const cartCount = this.#cartCount;
    const key = `${cartCount}:${selected}`;
    if (key === this.#renderedKey) return;
    this.#renderedKey = key;

    const { tiers = [], track, status } = this.refs;
    if (tiers.length === 0) return;

    const totalCount = cartCount + selected;
    const inCart = this.#progressFor(tiers, cartCount);
    const withThis = this.#progressFor(tiers, totalCount);

    for (const tier of tiers) {
      const quantity = Number(tier.dataset.quantity);
      const isUnlocked = cartCount >= quantity;
      const isPreview = !isUnlocked && totalCount >= quantity;
      const wasReached = tier.classList.contains('is-unlocked') || tier.classList.contains('is-preview');

      tier.classList.toggle('is-unlocked', isUnlocked);
      tier.classList.toggle('is-preview', isPreview);
      if (animate && (isUnlocked || isPreview) && !wasReached) replay(tier, 'is-celebrating');
    }

    const maxQuantity = Math.max(...tiers.map((tier) => Number(tier.dataset.quantity) || 0));
    track.style.setProperty('--cp-promo-progress', String(inCart.fill));
    track.style.setProperty('--cp-promo-preview', String(withThis.fill));
    track.setAttribute('aria-valuemax', String(maxQuantity));
    track.setAttribute('aria-valuenow', String(Math.min(cartCount, maxQuantity)));

    const sentence = this.#buildStatus(inCart, withThis, selected, totalCount);
    if (sentence.textContent !== status.textContent) {
      status.replaceChildren(sentence);
      if (animate) replay(status, 'is-changing');
    }
  }

  /**
   * "Unlocked: <cart offer>." for what the cart already has, then what adding this product does:
   * - reaches a new tier: "Add this item to unlock <offer>[, then N more for <next>]."
   * - falls short: "Add this item plus N more to unlock <offer>."
   * Merchant text only ever lands in text nodes.
   * @param {TierProgress} inCart
   * @param {TierProgress} withThis
   * @param {number} selected
   * @param {number} totalCount
   * @returns {DocumentFragment}
   */
  #buildStatus(inCart, withThis, selected, totalCount) {
    const { dataset } = this;
    const fragment = document.createDocumentFragment();
    const plural = selected === 1 ? 'One' : 'Other';

    if (!inCart.next) {
      appendTemplate(fragment, dataset.templateBest, { offer: inCart.reached?.dataset.title });
      return fragment;
    }

    if (inCart.reached) {
      appendTemplate(fragment, dataset.templateUnlocked, { offer: inCart.reached.dataset.title });
      fragment.append(' ');
    }

    const count = String(selected);
    if (withThis.reached && withThis.reached !== inCart.reached) {
      if (withThis.next) {
        appendTemplate(fragment, dataset[`templateAddThen${plural}`], {
          count,
          more: String(Number(withThis.next.dataset.quantity) - totalCount),
          offer: withThis.reached.dataset.title,
          next: withThis.next.dataset.title,
        });
      } else {
        appendTemplate(fragment, dataset[`templateAdd${plural}`], { count, offer: withThis.reached.dataset.title });
      }
    } else {
      appendTemplate(fragment, dataset[`templateAddMore${plural}`], {
        count,
        more: String(Number(inCart.next.dataset.quantity) - totalCount),
        offer: inCart.next.dataset.title,
      });
    }

    return fragment;
  }
}

/**
 * Fills `[count]` and `[more]` as text and turns `[offer]` and `[next]` into highlighted names.
 * @param {DocumentFragment} fragment
 * @param {string | undefined} template
 * @param {{ count?: string, more?: string, offer?: string, next?: string }} values
 */
function appendTemplate(fragment, template = '', values) {
  const text = template.replace('[count]', values.count ?? '').replace('[more]', values.more ?? '');

  for (const part of text.split(/(\[offer\]|\[next\])/)) {
    if (part === '[offer]' || part === '[next]') {
      const highlight = document.createElement('strong');
      highlight.className = 'cp-promo__highlight';
      highlight.textContent = (part === '[offer]' ? values.offer : values.next) ?? '';
      fragment.append(highlight);
    } else if (part) {
      fragment.append(part);
    }
  }
}

/**
 * Restarts a one-shot CSS animation class.
 * @param {HTMLElement} element
 * @param {string} className
 */
function replay(element, className) {
  element.classList.remove(className);
  void element.offsetWidth;
  element.classList.add(className);
  element.addEventListener('animationend', () => element.classList.remove(className), { once: true });
}

if (!customElements.get('cp-promo-component')) {
  customElements.define('cp-promo-component', CpPromoComponent);
}
